import { useState } from 'react';
import classNames from 'classnames';

import Loader from 'components/partials/loader/loader';

import type { InsightCommentDto } from 'api/generated/schemas/insightCommentDto';

export type InsightScreenshotComment = InsightCommentDto;

interface InsightScreenshotProps {
  imageUrl?: string;
  comments?: InsightScreenshotComment[];
  selectedCommentId: string | number | null;
  onCommentClick: (commentId: string | number) => void;
}

const InsightScreenshot = ({
  imageUrl,
  comments = [],
  selectedCommentId,
  onCommentClick,
}: InsightScreenshotProps): React.JSX.Element => {
  const [isImageLoading, setIsImageLoading] = useState<boolean>(true);

  if (!imageUrl) {
    return (
      <div className="bg-gray-100 p-8 text-center">
        <p className="text-gray-500">No screenshot available</p>
      </div>
    );
  }

  return (
    <div
      className={classNames('overflow-hidden p-3 relative', {
        'p-5': isImageLoading,
      })}
    >
      <Loader
        loading={isImageLoading}
        customClass="!bg-white"
        fullScreen={false}
      />

      <div className={classNames('relative', { invisible: isImageLoading })}>
        <img
          src={imageUrl}
          alt="Insight screenshot"
          onLoad={() => setIsImageLoading(false)}
          onError={() => setIsImageLoading(false)}
        />

        {comments.map((comment, index) => {
          const isSelected = selectedCommentId === comment.id;
          const commentNumber = index + 1;

          const leftPercent = (comment.positionXRelative || 0) * 100;
          const topPercent = (comment.positionYRelative || 0) * 100;

          return (
            <button
              key={comment.id ?? `pin-${commentNumber}`}
              className={classNames(
                'absolute size-7 rounded-full z-20 flex items-center justify-center text-white text-sm font-bold bg-navy-400',
                {
                  '!bg-navy-800': isSelected,
                },
              )}
              style={{
                left: `${leftPercent}%`,
                top: `${topPercent}%`,
                transform: 'translate(-50%, -50%)',
              }}
              onClick={() => {
                if (comment.id !== undefined) {
                  onCommentClick(comment.id);
                }
              }}
              title={comment.message}
            >
              {commentNumber}
            </button>
          );
        })}
      </div>
    </div>
  );
};

export default InsightScreenshot;
